import playerMessages from '@/lang/messages/player';
import { EnumButtonName, EventProperties } from '@calm/evexia-analytics';
import { Box, Button, SxProps, Theme } from '@rhg/libs/material';
import { Pause, PlayArrow, Shuffle } from '@rhg/libs/material/icons';
import { useIntl } from 'react-intl';

export interface PlaybackCtasProps {
    isPlaying: boolean;
    isShuffleOn: boolean;
    onPlay: () => void;
    onShuffle: () => void;
    isPlayDisabled?: boolean;
    isShuffleDisabled?: boolean;
    playTrackButton?: EnumButtonName;
    playTrackProps?: EventProperties;
    shuffleTrackButton?: EnumButtonName;
    shuffleTrackProps?: EventProperties;
}

const styleRow = {
    display: 'flex',
    gap: 4,
    alignItems: 'center',
    flexWrap: 'wrap',
};

// Mobile (below md): the two buttons split the row 50/50 via flex:1 (equal
// share after the shared gap). Desktop (md+): flex:0 0 auto so minWidth governs
// — each button is at least the medium button-width token, overflow is fine.
const styleControlButton = {
    flex: { xs: 1, md: '0 0 auto' },
    minWidth: (theme: Theme) => ({ md: theme.ds.width.BUTTON_MEDIUM }),
};

// Active-shuffle indicator dot drawn under the glyph — mirrors the in-player
// ShuffleButton/LoopButton. The CTA indicates shuffle state with the dot only;
// the icon color does not change (EVE-11935).
const styleIconWrapper = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
};

const styleActiveDot: SxProps<Theme> = {
    position: 'absolute',
    bottom: '-0.2em',
    width: '0.2em',
    height: '0.2em',
    borderRadius: '50%',
    // Match the icon it indicates: the icon renders `inherit` (white in dark),
    // so use currentColor there; keep the info accent in light. A function
    // value is emitted as-is, so light returns the resolved semantics color
    // (EVE-11935).
    bgcolor: (theme: Theme) =>
        theme.palette.mode === 'dark'
            ? 'currentColor'
            : theme.semantics.Icon.Feedback.Info,
};

/** Play/Pause and Shuffle header pills. Stateless: callers own playback and shuffle state. */
export function PlaybackCtas({
    isPlaying,
    isShuffleOn,
    onPlay,
    onShuffle,
    isPlayDisabled = false,
    isShuffleDisabled = false,
    playTrackButton,
    playTrackProps,
    shuffleTrackButton,
    shuffleTrackProps,
}: PlaybackCtasProps) {
    const { formatMessage } = useIntl();
    return (
        <Box sx={styleRow}>
            <Button
                variant="contained"
                size="large"
                sx={styleControlButton}
                startIcon={isPlaying ? <Pause /> : <PlayArrow />}
                onClick={onPlay}
                disabled={isPlayDisabled}
                {...(playTrackButton
                    ? {
                          trackButton: playTrackButton,
                          trackProps: playTrackProps,
                      }
                    : { skipTracking: true as const })}
            >
                {formatMessage(
                    playerMessages[isPlaying ? 'player.pause' : 'player.play']
                )}
            </Button>
            <Button
                variant="outlined"
                size="large"
                sx={styleControlButton}
                startIcon={
                    <Box sx={styleIconWrapper}>
                        <Shuffle color="inherit" />
                        {isShuffleOn && (
                            <Box
                                data-testid="shuffle-dot"
                                aria-hidden="true"
                                sx={styleActiveDot}
                            />
                        )}
                    </Box>
                }
                aria-pressed={isShuffleOn}
                onClick={onShuffle}
                disabled={isShuffleDisabled}
                {...(shuffleTrackButton
                    ? {
                          trackButton: shuffleTrackButton,
                          trackProps: shuffleTrackProps,
                      }
                    : { skipTracking: true as const })}
            >
                {formatMessage(playerMessages['player.shuffle'])}
            </Button>
        </Box>
    );
}
